import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  EditableEvent,
  blockingEventFields,
  buildEventForm,
  eventFieldError,
  eventFormPayload,
} from '../../components/space-claim/event-form';
import { UsersService } from '../../services/users-service.service';
import { GetEventToClaimDto } from '../../data-transfer-objects/get-event-to-claim.dto';
import { HttpParams } from '@angular/common/http';
import { LogoUploadComponent } from '../../components/logo-upload.component';
import { AgendaUploadComponent } from '../../components/agenda-upload.component';
import { SpaceClaimHeaderComponent } from '../../components/space-claim/space-claim-header.component';
import { SpaceClaimStatusComponent } from '../../components/space-claim/space-claim-status.component';
import { FormSectionComponent } from '../../components/space-claim/form-section.component';
import { QrCodeDisplayComponent } from '../../components/space-claim/qr-code-display.component';
import { WizardHeaderComponent } from '../../components/space-claim/wizard-header.component';
import { SpacePreviewComponent } from '../../components/space-claim/space-preview.component';
import { EventTooLongNoticeComponent } from '../../components/space-claim/event-too-long-notice.component';
import { environment } from '../../../environments/environment';
import * as QRCode from 'qrcode'; 

@Component({
  selector: 'app-space-claim',
  templateUrl: './space-claim.component.html',
  styleUrls: ['./space-claim.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    LogoUploadComponent, 
    AgendaUploadComponent,
    SpaceClaimHeaderComponent,
    SpaceClaimStatusComponent,
    FormSectionComponent,
    QrCodeDisplayComponent,
    WizardHeaderComponent,
    SpacePreviewComponent,
    EventTooLongNoticeComponent
  ],
})
export class SpaceClaimComponent implements OnInit {
  eventForm!: FormGroup;
  eventData?: GetEventToClaimDto;
  loading = true;
  isUpdating = false;
  currentStep = 1;
  eventTypes = ['Event', 'Community'];
  registrationModes = ['Public', 'Private'];
  
  // QR Code properties
  showQRModal = false;
  isGeneratingQR = false;
  qrCodeData: string | null = null;
  deepLinkUrl: string | null = null;
  qrError: string | null = null;
  
  // Success Modal properties
  showSuccessModal = false;
  
  // Already Claimed state
  isAlreadyClaimed = false;

  /*
   * Errors are normally hidden until a field is touched, but every field here
   * arrives pre-filled from the invitation, so an invalid value the organizer
   * never typed would stay silent. Once they try to submit, show everything.
   */
  submitAttempted = false;

  constructor(private usersService: UsersService, private fb: FormBuilder) {}

  // Custom validator to ensure end date is equal to or greater than start date

  ngOnInit(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const eventID = urlParams.get('eventID');
    console.log('SpaceClaimComponent: eventID from URL:', eventID);
    this.usersService.getSpaceToClaim(new HttpParams()).subscribe({
      next: (event: GetEventToClaimDto) => {
        console.log('SpaceClaimComponent: event data received:', event);
        if (!event || !event._id) {
          this.eventData = undefined;
        } else {
          // Check if the space is already claimed
          if (event.ownerId && event.ownerId.trim() !== '') {
            this.isAlreadyClaimed = true;
            this.eventData = undefined;
          } else {
            this.eventData = event;
            this.initializeForm(event);
          }
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('SpaceClaimComponent: error loading event:', err);
        this.eventData = undefined;
        this.loading = false;
      }
    });
  }

  private initializeForm(event: GetEventToClaimDto): void {
    /* Fields, validators and the date rule are shared with the admin events
       board, which edits the same row. See components/space-claim/event-form.ts. */
    this.eventForm = buildEventForm(this.fb, event as EditableEvent);
  }

  clearEndDate(): void {
    const endDate = this.eventForm.get('endDate');
    endDate?.setValue('');
    endDate?.markAsDirty();
    endDate?.updateValueAndValidity();
  }


  onLogoUploaded(logoUrl: string): void {
    this.eventForm.patchValue({ logoUrl });
    console.log('Logo uploaded:', logoUrl);
  }

  onLogoRemoved(): void {
    this.eventForm.patchValue({ logoUrl: '' });
    console.log('Logo removed');
  }

  // Method removed as we're no longer using a modal

  onAgendaUploaded(agendaUrl: string): void {
    this.eventForm.patchValue({ brochureUrl: agendaUrl });
    console.log('Agenda uploaded:', agendaUrl);
  }

  onAgendaRemoved(): void {
    this.eventForm.patchValue({ brochureUrl: '' });
    console.log('Agenda removed');
  }

  goBackToStep1(): void {
    this.currentStep = 1;
  }

  onSubmit() {
    this.submitAttempted = true;
    if (!this.eventForm.valid) {
      // Surface validation messages to user
      this.eventForm.markAllAsTouched();
      this.scrollToFirstError();
      return;
    }
    if (this.eventForm.valid && !this.isUpdating) {
      this.isUpdating = true;
      
      const updateData = eventFormPayload(this.eventForm);

      this.usersService.updateSpaceToClaim(this.eventData!._id, updateData).subscribe({
        next: () => {
          console.log('Space updated successfully');
          // Move to Step 2 and generate QR code inline
          this.currentStep = 2;
          this.generateQRCode();
          this.isUpdating = false;
        },
        error: (error) => {
          console.error('Error updating space:', error);
          alert('Error updating space. Please try again.');
          this.isUpdating = false;
        }
      });
    }
  }

  getFieldError(fieldName: string): string {
    return eventFieldError(this.eventForm, fieldName, this.submitAttempted);
  }

  /* Names of the fields currently blocking submission, for the summary banner. */
  get blockingFields(): string[] {
    return this.eventForm ? blockingEventFields(this.eventForm) : [];
  }

  private scrollToFirstError(): void {
    const firstInvalid = Object.keys(this.eventForm.controls).find(
      (name) => this.eventForm.get(name)?.invalid,
    );
    if (!firstInvalid) return;
    document.getElementById(firstInvalid)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  generateQRCode(): void {
    if (!this.eventData?._id) {
      console.error('No event ID available');
      return;
    }
    this.showQRModal = true;
    this.isGeneratingQR = true;
    this.qrCodeData = null;
    this.deepLinkUrl = null;
    this.qrError = null;
    
    const apiUrl = `${environment.spaceClaimBaseURL}/${this.eventData._id}/deeplink`;
    
    fetch(apiUrl)
      .then(response => {
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON');
        }
        return response.json();
      })
      .then(data => {
        this.isGeneratingQR = false;
        console.log('Deep link response:', data);
        if (data.deepLinkUrl) {
          this.deepLinkUrl = data.deepLinkUrl;
          // Generate QR code on the frontend
          return QRCode.toDataURL(data.deepLinkUrl, {
            width: 300,
            margin: 2,
            color: {
              dark: "#000000",
              light: "#FFFFFF"
            }
          });
        } else {
          throw new Error(data.error || 'Failed to get deep link URL');
        }
      })
      .then(qrCodeDataUrl => {
        this.qrCodeData = qrCodeDataUrl;
        console.log('QR code generated successfully');
      })
      .catch(error => {
        this.isGeneratingQR = false;
        console.error('Error generating QR code:', error);
        if (error.message.includes('<!DOCTYPE')) {
          this.qrError = 'Firebase Function not available. Please try again later.';
        } else {
          this.qrError = 'Network error: ' + error.message;
        }
      });
  }

  closeQRModal(): void {
    this.showQRModal = false;
    this.qrCodeData = null;
    this.deepLinkUrl = null;
    this.qrError = null;
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
  }


}
